import { Routes, Route, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import { ProtectedRoute } from "~/app/protected-route";
import { FullscreenLayout } from "~/app/fullscreen-layout";
import { ChatEmptyPage, ChatPage } from "~/pages/chat";

function PageLoader() {
    return (
        <div className="flex h-screen items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-foreground" />
        </div>
    );
}

// Lazy-loaded pages — each route is a separate chunk
const Home = lazy(() => import("~/pages/landing/home"));
const Video = lazy(() => import("~/pages/landing/video"));
const Images = lazy(() => import("~/pages/landing/images"));
const Pro = lazy(() => import("~/pages/landing/pro"));
const Deal = lazy(() => import("~/pages/landing/deal"));
const Start = lazy(() => import("~/pages/landing/start"));
const Economy = lazy(() => import("~/pages/landing/economy"));
const Privacy = lazy(() => import("~/pages/landing/privacy"));
const Oferta = lazy(() => import("~/pages/landing/oferta"));
const Login = lazy(() => import("~/pages/auth/login"));
const AuthVerify = lazy(() => import("~/pages/auth/verify"));
const AuthCleanup = lazy(() => import("~/pages/auth/cleanup"));
const AppLayout = lazy(() => import("~/app/layout").then((m) => ({ default: m.AppLayout })));
const AdminPage = lazy(() => import("~/pages/admin"));
const Invitation = lazy(() => import("~/pages/invitation"));
const ProjectInvite = lazy(() => import("~/pages/project-invite"));
const SettingsProfileLayout = lazy(() => import("~/pages/settings/layout"));
const SettingsProfile = lazy(() => import("~/pages/settings/profile"));
const SettingsAccountDanger = lazy(() => import("~/pages/settings/danger"));
const SettingsWorkspaceLayout = lazy(() => import("~/pages/settings/workspace/layout"));
const SettingsWorkspaceIdentity = lazy(() => import("~/pages/settings/workspace/identity"));
const SettingsWorkspaceMembers = lazy(() => import("~/pages/settings/workspace/members"));
const SettingsWorkspaceBilling = lazy(() => import("~/pages/settings/workspace/billing"));
const SettingsWorkspaceDanger = lazy(() => import("~/pages/settings/workspace/danger"));
const ProjectsList = lazy(() => import("~/pages/projects/list"));
const ProjectDetail = lazy(() => import("~/pages/projects/detail"));
const ChatsList = lazy(() => import("~/pages/chats/list"));
const AssetsList = lazy(() => import("~/pages/assets/list"));
const AssetFolder = lazy(() => import("~/pages/assets/folder"));
const ElementsList = lazy(() => import("~/pages/elements/list"));
const AssetReview = lazy(() => import("~/pages/assets/review"));
const BoardList = lazy(() => import("~/pages/board/list"));
const BoardEditor = lazy(() => import("~/pages/board/editor"));

export function AppRoutes() {
    return (
        <Suspense fallback={<PageLoader />}>
            <ProtectedRoute>
                <Routes>
                    {/* Public */}
                    <Route path="/" element={<Home />} />
                    <Route path="/video" element={<Video />} />
                    <Route path="/images" element={<Images />} />
                    <Route path="/pro" element={<Pro />} />
                    <Route path="/deal" element={<Deal />} />
                    <Route path="/start" element={<Start />} />
                    <Route path="/economy" element={<Economy />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/oferta" element={<Oferta />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/auth/verify" element={<AuthVerify />} />
                    <Route path="/auth/cleanup" element={<AuthCleanup />} />
                    {/* Protected */}
                    {/* Legacy redirects */}
                    <Route path="/workspace" element={<Navigate replace to="/settings/workspace" />} />
                    <Route path="/profile" element={<Navigate replace to="/settings/profile" />} />
                    {/* Projects surface */}
                    <Route path="/projects" element={<AppLayout />}>
                        <Route index element={<ProjectsList />} />
                        <Route path=":projectId" element={<ProjectDetail />} />
                    </Route>
                    <Route path="/admin" element={<AppLayout />}>
                        <Route index element={<AdminPage />} />
                    </Route>
                    <Route path="/invitation" element={<Invitation />} />
                    <Route path="/project-invite" element={<ProjectInvite />} />
                    <Route path="/chat" element={<AppLayout />}>
                        <Route index element={<ChatEmptyPage />} />
                        <Route path=":chatId" element={<ChatPage />} />
                    </Route>
                    <Route path="/chats" element={<AppLayout />}>
                        <Route index element={<ChatsList />} />
                    </Route>
                    <Route path="/assets" element={<AppLayout />}>
                        <Route index element={<AssetsList />} />
                        <Route path="folders/:folderId" element={<AssetFolder />} />
                    </Route>
                    <Route path="/generations" element={<Navigate replace to="/assets" />} />
                    <Route path="/elements" element={<AppLayout />}>
                        <Route index element={<ElementsList />} />
                    </Route>
                    {/* Whiteboard: list under the normal app layout, editor full-screen */}
                    <Route path="/boards" element={<AppLayout />}>
                        <Route index element={<BoardList />} />
                    </Route>
                    <Route element={<FullscreenLayout />}>
                        <Route path="/boards/:boardId" element={<BoardEditor />} />
                    </Route>
                    {/* Ревью — full-screen вьювер без сайдбара, но с workspace/portal-провайдерами
                        (spec §10). Без FullscreenLayout useActiveWorkspace кидает → пустой экран. */}
                    <Route element={<FullscreenLayout />}>
                        <Route path="/assets/folders/:folderId/review" element={<AssetReview />} />
                    </Route>
                    <Route path="/settings" element={<AppLayout />}>
                        <Route index element={<Navigate to="profile" replace />} />
                        <Route element={<SettingsProfileLayout />}>
                            <Route path="profile" element={<SettingsProfile />} />
                            <Route path="profile/danger" element={<SettingsAccountDanger />} />
                        </Route>
                        <Route path="danger" element={<Navigate to="/settings/profile/danger" replace />} />
                        <Route path="workspace" element={<SettingsWorkspaceLayout />}>
                            <Route index element={<SettingsWorkspaceIdentity />} />
                            <Route path="members" element={<SettingsWorkspaceMembers />} />
                            <Route path="billing" element={<SettingsWorkspaceBilling />} />
                            <Route path="danger" element={<SettingsWorkspaceDanger />} />
                        </Route>
                    </Route>
                </Routes>
            </ProtectedRoute>
        </Suspense>
    );
}
